import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../../_redux/user-assignment/UserAssignmentAction";
import {
  copy,
  DeleteButton,
  ExportButton,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  removalAlert,
  resultAlert
} from "../../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../../_metronic/_partials/controls";
import { useParentUsersUIContext } from "./ParentUsersUIContext";
import { ParentUsersFilter } from "./parent-users-filter/ParentUsersFilter";
import { ParentUsersTable } from "./parent-users-table/ParentUsersTable";

export function ParentUsersCard() {
  // Parent Users Card UI Context
  const parentUsersUIContext = useParentUsersUIContext();
  const parentUsersUIProps = useMemo(() => {
    return {
      ids: parentUsersUIContext.ids,
      setIds: parentUsersUIContext.setIds,
      queryParams: parentUsersUIContext.queryParams
    };
  }, [parentUsersUIContext]);

  // Getting curret state of UserAssignment from store (Redux)
  const { currentState, currentActiveTerm } = useSelector(
    (state) => ({
      currentState: state.userAssignment,
      currentActiveTerm: state.auth.user.currentActiveTerm
    }),
    shallowEqual
  );  
  const { actionsLoading, isSuccess, type, entities, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // Parent Users Redux state
  const dispatch = useDispatch();
    
  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Veli Kullanıcı ve Kullanıcı Grubu Listesini Excele Aktarır */
  const exportParentUsersToExcel = () => {
    dispatch(actions.getAllParentUserAndUserGroups(parentUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [30, 30, 30, 30, 25, 30];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Öğrenci", "Şube", "Kullanıcı Grubu"];
        
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.studentName,
        entity.section,
        entity.userGroup
      ]));
        
      const filename = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Veli Kullanıcı ve Kullanıcı Grubu Listesi";
      const sheetname = "Veli Kullanıcı Listesi";
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Veli Kullanıcı ve Kullanıcı Grubu Listesini PDF e Aktarır */
  const exportParentUsersToPDF = () => {
    dispatch(actions.getAllParentUserAndUserGroups(parentUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "TC Kimlik No",
          rows: entities.map(entity => entity.tcKimlikNo)
        },
        {
          header: "Adı",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Soyadı",
          rows: entities.map(entity => entity.surname)
        },
        {
          header: "Öğrenci",
          rows: entities.map(entity => entity.studentName)
        },
        {
          header: "Şube",
          rows: entities.map(entity => entity.section)
        },
        {
          header: "Kullanıcı Grubu",
          rows: entities.map(entity => entity.userGroup)
        }
      ];
    
      const filename = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Veli Kullanıcı ve Kullanıcı Grubu Listesi";
      exportToPDF(views, filename);
    });
  }
  
  /** Belli Bir Veli Kullanıcı ve Kullanıcı Grubu Listesini Yazdırma Penceresini Açar */
  const printParentUsers = () => {
    dispatch(actions.getAllParentUserAndUserGroups(parentUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Veli Kullanıcı ve Kullanıcı Grubu Listesi";
      const properties = ["TC Kimlik No", "Adı", "Soyadı", "Öğrenci", "Şube", "Kullanıcı Grubu"];
    
      entities.forEach(entity => {
        data.push({
          "TC Kimlik No": entity.tcKimlikNo,
          "Adı": entity.name,
          "Soyadı": entity.surname,
          "Öğrenci": entity.studentName,
          "Şube": entity.section,
          "Kullanıcı Grubu": entity.userGroup
        });
      });
    
      print({ data, properties, header });
    });    
  }
  
  /** Belli Bir Veli Kullanıcı ve Kullanıcı Grubu Listesini Panoya Kopyalar */
  const copyParentUsers = () => {
    dispatch(actions.getAllParentUserAndUserGroups(parentUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Öğrenci", "Şube", "Kullanıcı Grubu"];
      const header = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Veli Kullanıcı ve Kullanıcı Grubu Listesi";
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.studentName,
        entity.section,
        entity.userGroup
      ]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Kullanıcı Listesini İlgili Kullanıcı Gruplarından Çıkarır */
  const removeUsers = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
    
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }

    const message = "İlgili kullanıcı(lar) kullanıcı grubu(ları)ndan çıkarılacaktır."
    removalAlert(message).then(result => {
      const selectedEntities = entities.filter(entity => ids.includes(entity.id));
      const userIds = [...new Set(selectedEntities.map(entity => entity.userId))];
      if (result.isConfirmed) dispatch(actions.removeUsers(userIds)).then(result => onResult(result));
    });
  }
  
  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
    
    if (isSuccess) {
      setTimeout(() => {
        parentUsersUIProps.setIds([]);
        dispatch(actions.getParentUserAndUserGroups(parentUsersUIProps.queryParams));
      }, 2500);
    }
  };

  const title = <>Veli Kullanıcı Listesi{parentUsersUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{parentUsersUIProps.ids.length}</span>]</> :
                   ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportParentUsersToExcel}
                exportToPDF={exportParentUsersToPDF}
                print={printParentUsers}
                copy={copyParentUsers}
              />
            }
            {
              isDeletionAuthorized &&
              <DeleteButton
                path="Navigation/Close"
                title="Seçilenleri Çıkar"
                method={() => removeUsers(parentUsersUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }
      </CardHeader>
      <CardBody>
        <ParentUsersFilter />
        <ParentUsersTable
          removeUsers={removeUsers}
        />
      </CardBody>
    </Card>
  );
}